import {
    Button,
    Card,
    Input,
    Modal,
    notification,
    Skeleton,
    Form,
    Switch,
    Select,
    Checkbox,
    Tabs,
    Popover,
    Popconfirm,
    Tag,
} from "antd";
import { useEffect, useState } from "react";
import HTTP from "../../services/httpService";
import CustomHeader from "../common/Header";
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    QuestionCircleOutlined,
    UserOutlined,
    MailOutlined,
    LockOutlined,
    AppstoreOutlined,
    TeamOutlined,
    SafetyOutlined,
    BankOutlined,
} from "@ant-design/icons";
import "./List.css";
import CustomTable from "../common/Table";
import { useQuery } from "react-query";
import { encrypt, getUserRole, Roles, vendorTypes } from "../../services/util";

const { Option } = Select;

const platforms = [
    {
        key: 1,
        name: "Analytics",
        color: "var(--color-primary)",
        bg: "var(--color-primary-light)",
        border: "var(--color-primary)",
    },
    {
        key: 2,
        name: "Vendor Portal",
        color: "var(--color-accent)",
        bg: "var(--color-accent-light)",
        border: "var(--color-accent)",
    },
    {
        key: 3,
        name: "Jira",
        color: "var(--color-primary-hover)",
        bg: "var(--color-primary-light)",
        border: "var(--color-primary-hover)",
    },
    {
        key: 4,
        name: "GIT",
        color: "var(--color-warning)",
        bg: "var(--color-warning-light)",
        border: "var(--color-warning)",
    },
    {
        key: 5,
        name: "Dynatrace",
        color: "var(--color-error)",
        bg: "var(--color-error-light)",
        border: "var(--color-error)",
    },
];

const VendorRoles = [
    { key: 1, name: "Admin" },
    { key: 2, name: "Support" },
    { key: 3, name: "Sales" },
    { key: 5, name: "Trainee" },
];

// ── Shared theme input style ──────────────────────────
const inputStyle = {
    background: "var(--color-bg-control)",
    border: "1px solid var(--color-border)",
    borderRadius: 8,
    color: "var(--color-text)",
    fontSize: 13,
};

// ── Modal theme styles ────────────────────────────────
const modalStyles = {
    content: {
        background: "var(--color-bg-card)",
        border: "1px solid var(--color-border)",
        borderRadius: 12,
        padding: 28,
    },
    header: {
        background: "var(--color-bg-card)",
        borderBottom: "1px solid var(--color-border)",
        paddingBottom: 16,
    },
    mask: { backdropFilter: "blur(4px)" },
};

// ── Helper components ─────────────────────────────────
function PlatformBadge({ value }) {
    const p = platforms.find((pl) => pl.key == value);
    if (!p) return <span style={{ color: "var(--color-text-muted)" }}>—</span>;
    return (
        <span
            style={{
                display: "inline-block",
                background: p.bg,
                border: `1px solid ${p.border}`,
                color: p.color,
                borderRadius: 6,
                padding: "2px 10px",
                fontWeight: 600,
                fontSize: 11,
            }}
        >
            {p.name}
        </span>
    );
}

function StatusBadge({ active }) {
    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                background: active
                    ? "var(--color-success-light)"
                    : "var(--color-error-light)",
                border: `1px solid ${
                    active ? "var(--color-success)" : "var(--color-error)"
                }`,
                color: active ? "var(--color-success)" : "var(--color-error)",
                borderRadius: 6,
                padding: "2px 10px",
                fontWeight: 600,
                fontSize: 11,
            }}
        >
            <span
                style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "currentColor",
                    flexShrink: 0,
                }}
            />
            {active ? "Active" : "Inactive"}
        </span>
    );
}

function UserAvatar({ firstName, lastName }) {
    const initials = `${firstName?.[0] || ""}${
        lastName?.[0] || ""
    }`.toUpperCase();
    return (
        <div
            style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                background: "var(--color-primary-light)",
                border: "1.5px solid var(--color-primary-light)",
                color: "var(--color-primary)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 12,
                flexShrink: 0,
            }}
        >
            {initials || "?"}
        </div>
    );
}

function FormLabel({ children }) {
    return (
        <span
            style={{
                color: "var(--color-text-muted)",
                fontSize: 12,
                fontWeight: 500,
            }}
        >
            {children}
        </span>
    );
}

function ModalHeader({ icon, title }) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
                style={{
                    width: 3,
                    height: 18,
                    borderRadius: 2,
                    background: "var(--gradient-primary)",
                }}
            />
            {icon && (
                <span style={{ color: "var(--color-primary)" }}>{icon}</span>
            )}
            <span
                style={{
                    color: "var(--color-text)",
                    fontWeight: 600,
                    fontSize: 16,
                }}
            >
                {title}
            </span>
        </div>
    );
}

// ── Action button helper ──────────────────────────────
function ActionBtn({ icon, color, bg, border, onClick, title }) {
    return (
        <Button
            type="text"
            size="small"
            title={title}
            icon={icon}
            onClick={onClick}
            style={{
                color,
                background: bg,
                border: `1px solid ${border}`,
                borderRadius: 7,
                width: 30,
                height: 30,
            }}
        />
    );
}

// ── Main component ────────────────────────────────────
function UserList() {
    const [sortedInfo, updateSortedInfo] = useState({
        order: "descend",
        columnKey: "first_name",
    });
    const [form] = Form.useForm();
    const [authForm] = Form.useForm();

    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const userRole = getUserRole();

    const [formData, setFormData] = useState({
        staff_id,
        page: 0,
        limit: 10,
        sort_by: sortedInfo.columnKey,
        sorting: sortedInfo.order === "ascend" ? 2 : 1,
    });

    const [hotelOptions, setHotelOptions] = useState([]);
    const [userData, setUsersData] = useState([]);
    const [unverifiedUsersData, setUnverifiedUsersData] = useState([]);
    const [visible, setVisible] = useState(false);
    const [authVisible, setAuthVisible] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalUnVerifiedRecords, setTotalUnVerifiedRecords] = useState(0);
    const [title, setTitle] = useState("Edit");
    const [userRoleOption, setUserRoleOption] = useState(Roles);
    const [activeTab, setActiveTab] = useState("users");
    const [unverifiedUserRole, setUnverifiedUserRole] = useState();

    // ── Queries ─────────────────────────────────────────
    const {
        status: userStatus,
        data: userRes,
        refetch: getUser,
    } = useQuery(
        "dashboard-users",
        async () => await HTTP("/users/getUsers", { ...formData }),
        { enabled: false },
    );
    const {
        status: unverifiedUserStatus,
        data: unVerifiedUserRes,
        refetch: getUnverifiedUser,
    } = useQuery(
        "dashboard-unverified-users",
        async () =>
            await HTTP("users/getUnverifiedSocialUsers", { ...formData }),
        { enabled: false },
    );
    const {
        status: hotelOptionStatus,
        data: hotelOptionRes,
        refetch: getHotelOptions,
    } = useQuery(
        "front-desk-hotels",
        async () =>
            await HTTP("/dashboard/getHotels", { staff_id, hotel_type: "all" }),
        { enabled: false },
    );

    useEffect(() => {
        getUser();
        getHotelOptions();
    }, []);
    useEffect(() => {
        if (hotelOptionStatus === "success")
            setHotelOptions(hotelOptionRes.data);
    }, [hotelOptionStatus, hotelOptionRes]);
    useEffect(() => {
        if (activeTab === "users") getUser();
        else getUnverifiedUser();
    }, [formData, activeTab]);
    useEffect(() => {
        if (userStatus === "success") {
            setUsersData(userRes.data);
            setTotalRecords(userRes.total);
        }
    }, [userStatus, userRes]);
    useEffect(() => {
        if (unverifiedUserStatus === "success") {
            setUnverifiedUsersData(unVerifiedUserRes.data);
            setTotalUnVerifiedRecords(unVerifiedUserRes.totalUnverified);
        }
    }, [unverifiedUserStatus, unVerifiedUserRes]);

    // ── Actions ──────────────────────────────────────────
    const verifyUser = (id, role_id) => {
        HTTP("/users/verifySocialUser", { id, role_id, staff_id }).then(
            ({ success, message }) => {
                notification[success === 0 ? "error" : "success"]({ message });
                if (success) getUnverifiedUser();
            },
        );
    };
    const deleteUnveridfiedUser = (id) => {
        HTTP("/users/deleteUnverifiedSocialUser", { id, staff_id }).then(
            ({ success, message }) => {
                notification[success === 0 ? "error" : "info"]({ message });
                if (success) getUnverifiedUser();
            },
        );
    };
    const rejectUnverifiedUser = (id) => {
        HTTP("/users/rejectSocialUser", { id, staff_id }).then(
            ({ success, message }) => {
                notification[success === 0 ? "error" : "info"]({ message });
                if (success) getUnverifiedUser();
            },
        );
    };

    async function deleteUser(fd) {
        const { success, message } = await HTTP("/users/deleteUsers", fd);
        notification[success === 0 ? "error" : "info"]({
            message: success === 0 ? message : "User deleted successfully.",
        });
        if (success) getUser();
        closeAuthModel();
    }

    function openAuthModel(id) {
        authForm.setFieldsValue({ id });
        setAuthVisible(true);
    }
    function updateUserRolesFromPlatform(p) {
        setUserRoleOption(p == 2 ? VendorRoles : Roles);
    }
    function updateUserRoleFormVendorUserType(vt) {
        const tmp = [...VendorRoles];
        setUserRoleOption(vt == 1 ? tmp.slice(0, 1) : tmp);
    }

    function editUser(e) {
        if (getUserRole() !== "Super Admin" && getUserRole() !== "Admin") {
            return notification.error({
                message: "You are not allowed to perform this action.",
            });
        }
        setUserRoleOption(Roles);
        const details = JSON.parse(e.currentTarget.getAttribute("details"));
        setTitle("Edit User");
        details.vendor_user_type =
            details.platform == 2 ? details.vendor_user_type : "";
        form.setFieldsValue(details);
        setVisible(true);
    }

    function openAddUserModel() {
        setTitle("Add User");
        form.resetFields();
        form.setFieldsValue({ platform: 1 });
        setVisible(true);
    }

    function closeModel() {
        setVisible(false);
        form.resetFields();
    }
    function closeAuthModel() {
        setAuthVisible(false);
        authForm.resetFields();
    }

    async function handleSubmit(fd) {
        if (!fd.hotel_ids) fd.hotel_ids = [];
        fd.all_selected = fd.hotel_ids.length === 0;
        fd.staff_id = staff_id;
        setVisible(false);
        form.resetFields();
        let url;
        if (fd.id) {
            if (fd.setPassword) fd.password = encrypt(fd.password);
            url = "/users/editUsers";
        } else {
            delete fd.id;
            fd.password = encrypt(fd.password);
            url = "/users/addUsers";
        }
        const { success, message } = await HTTP(url, fd);
        notification[success === 0 ? "error" : "info"]({ message });
        getUser();
    }

    function handleTableChange(pagination, filters, sorter) {
        const { current, pageSize } = pagination;
        const { order, field } = sorter;
        const others = {
            page: current - 1,
            limit: pageSize,
            ...(filters.platform ? { filters } : { filters: {} }),
        };
        if (order !== undefined) {
            others.sorting = order === "ascend" ? 1 : 2;
            others.sort_by = field;
        }
        setFormData({ staff_id, ...others });
    }

    // ── Table columns ─────────────────────────────────
    const columns = [
        {
            title: "User",
            key: "user",
            sorter: () => {},
            sortOrder:
                sortedInfo.columnKey === "first_name" ? sortedInfo.order : null,
            render: (_, r) => (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <UserAvatar
                        firstName={r.first_name}
                        lastName={r.last_name}
                    />
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                        }}
                    >
                        <span
                            style={{
                                color: "var(--color-text)",
                                fontSize: 13,
                                fontWeight: 500,
                            }}
                        >
                            {r.first_name} {r.last_name}
                        </span>
                        <span
                            style={{
                                color: "var(--color-text-muted)",
                                fontSize: 11,
                            }}
                        >
                            {r.email}
                        </span>
                    </div>
                </div>
            ),
        },
        {
            title: "Status",
            width: 100,
            render: (_, r) => <StatusBadge active={r.is_active} />,
        },
        {
            title: "Role",
            dataIndex: "role_id",
            width: 130,
            render: (text, r) => {
                const role =
                    r.platform == 1
                        ? getUserRole(text)
                        : VendorRoles.find(({ key }) => key == text)?.name;
                return role ? (
                    <span
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            background: "var(--color-bg-elevated)",
                            border: "1px solid var(--color-border)",
                            color: "var(--color-text-secondary)",
                            borderRadius: 6,
                            padding: "2px 10px",
                            fontSize: 12,
                        }}
                    >
                        <SafetyOutlined style={{ fontSize: 10 }} /> {role}
                    </span>
                ) : (
                    <span style={{ color: "var(--color-text-muted)" }}>—</span>
                );
            },
        },
        {
            title: "Vendor Type",
            dataIndex: "vendor_user_type",
            width: 110,
            render: (text, r) =>
                r.platform == 2 ? (
                    <span
                        style={{
                            color: "var(--color-text-muted)",
                            fontSize: 12,
                        }}
                    >
                        {text == 1 ? "Master" : "Hotel"}
                    </span>
                ) : (
                    <span style={{ color: "var(--color-text-muted)" }}>—</span>
                ),
        },
        {
            title: "Platform",
            dataIndex: "platform",
            width: 130,
            filters: platforms.map((v) => ({ text: v.name, value: v.key })),
            render: (text) => <PlatformBadge value={text} />,
        },
        {
            title: "Actions",
            fixed: "right",
            width: 80,
            render: (_, record) =>
                getUserRole(record.role_id) !== "Super Admin" &&
                staff_id != record.id ? (
                    <div style={{ display: "flex", gap: 6 }}>
                        {(userRole === "Super Admin" ||
                            userRole === "Admin") && (
                            <ActionBtn
                                title="Delete user"
                                icon={
                                    <DeleteOutlined
                                        onClick={() => openAuthModel(record.id)}
                                    />
                                }
                                color="var(--color-error)"
                                bg="var(--color-error-light)"
                                border="var(--color-error)"
                            />
                        )}
                        {record.platform == 1 && (
                            <ActionBtn
                                title="Edit user"
                                icon={
                                    <EditOutlined
                                        id={record.id}
                                        details={JSON.stringify(record)}
                                        onClick={editUser}
                                    />
                                }
                                color="var(--color-accent)"
                                bg="var(--color-accent-light)"
                                border="var(--color-accent)"
                            />
                        )}
                    </div>
                ) : null,
        },
    ];

    const unvarifiedUserColumns = [
        {
            title: "Name",
            key: "name",
            render: (_, r) => (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <UserAvatar
                        firstName={r.first_name}
                        lastName={r.last_name}
                    />
                    <span
                        style={{
                            color: "var(--color-text)",
                            fontSize: 13,
                            fontWeight: 500,
                        }}
                    >
                        {r.first_name} {r.last_name}
                    </span>
                </div>
            ),
            sorter: () => {},
        },
        {
            title: "Email",
            dataIndex: "email",
            sorter: () => {},
            render: (email) => (
                <span
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        color: "var(--color-text-muted)",
                        fontSize: 13,
                    }}
                >
                    <MailOutlined style={{ fontSize: 11 }} /> {email}
                </span>
            ),
        },
        {
            title: "Actions",
            width: 130,
            render: (_, record) => (
                <div style={{ display: "flex", gap: 6 }}>
                    <Popconfirm
                        title="Delete this user?"
                        description="This cannot be undone."
                        icon={
                            <QuestionCircleOutlined
                                style={{ color: "var(--color-error)" }}
                            />
                        }
                        onConfirm={() => deleteUnveridfiedUser(record.id)}
                        okText="Delete"
                        cancelText="Cancel"
                    >
                        <ActionBtn
                            title="Delete"
                            icon={<DeleteOutlined />}
                            color="var(--color-error)"
                            bg="var(--color-error-light)"
                            border="var(--color-error)"
                        />
                    </Popconfirm>

                    {!record?.is_verified && !record?.is_rejected && (
                        <Popover
                            placement="leftTop"
                            trigger="click"
                            title={
                                <span style={{ color: "var(--color-text)" }}>
                                    Assign Role
                                </span>
                            }
                            content={
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 8,
                                        minWidth: 180,
                                    }}
                                >
                                    <Select
                                        allowClear
                                        placeholder="Select role"
                                        onChange={setUnverifiedUserRole}
                                        value={unverifiedUserRole}
                                        style={{ width: "100%" }}
                                    >
                                        {Roles.map((r) => (
                                            <Option key={r.key}>
                                                {r.name}
                                            </Option>
                                        ))}
                                    </Select>
                                    <Button
                                        size="small"
                                        type="primary"
                                        style={{
                                            alignSelf: "flex-end",
                                            borderRadius: 6,
                                        }}
                                        onClick={() =>
                                            verifyUser(
                                                record.id,
                                                unverifiedUserRole,
                                            )
                                        }
                                    >
                                        Verify & Approve
                                    </Button>
                                </div>
                            }
                        >
                            <ActionBtn
                                title="Verify user"
                                icon={<CheckCircleOutlined />}
                                color="var(--color-success)"
                                bg="var(--color-success-light)"
                                border="var(--color-success)"
                            />
                        </Popover>
                    )}

                    {!record?.is_rejected && (
                        <ActionBtn
                            title="Reject user"
                            icon={<CloseCircleOutlined />}
                            color="var(--color-warning)"
                            bg="var(--color-warning-light)"
                            border="var(--color-warning)"
                            onClick={() => rejectUnverifiedUser(record.id)}
                        />
                    )}
                </div>
            ),
        },
    ];

    const isUsersLoading = userStatus === "loading" || userStatus === "idle";
    const isUnverifiedLoading =
        unverifiedUserStatus === "loading" || unverifiedUserStatus === "idle";

    // Summary counts
    const activeUsers = userData.filter((u) => u.is_active).length;
    const inactiveUsers = userData.filter((u) => !u.is_active).length;

    return (
        <>
            <Card className="dashboard-container">
                <CustomHeader />

                {/* ── Page header ── */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        margin: "16px 0",
                    }}
                >
                    <div>
                        <h2
                            style={{
                                fontSize: 22,
                                fontWeight: 700,
                                color: "var(--color-text)",
                                margin: 0,
                                letterSpacing: "-0.01em",
                            }}
                        >
                            User Management
                        </h2>
                        <p
                            style={{
                                fontSize: 13,
                                color: "var(--color-text-muted)",
                                margin: "4px 0 0",
                            }}
                        >
                            Manage access, roles and platform permissions
                        </p>
                    </div>
                    {(userRole === "Super Admin" || userRole === "Admin") && (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={openAddUserModel}
                        >
                            Add User
                        </Button>
                    )}
                </div>

                {/* ── Summary pills ── */}
                {!isUsersLoading && (
                    <div
                        style={{
                            display: "flex",
                            gap: 12,
                            marginBottom: 16,
                            flexWrap: "wrap",
                        }}
                    >
                        {[
                            {
                                label: "Total Users",
                                value: totalRecords,
                                color: "var(--color-primary)",
                            },
                            {
                                label: "Active",
                                value: activeUsers,
                                color: "var(--color-success)",
                            },
                            {
                                label: "Inactive",
                                value: inactiveUsers,
                                color: "var(--color-error)",
                            },
                            {
                                label: "Pending Approval",
                                value: totalUnVerifiedRecords,
                                color: "var(--color-warning)",
                            },
                        ].map(({ label, value, color }) => (
                            <div
                                key={label}
                                style={{
                                    background: "var(--color-bg-card)",
                                    border: `1px solid ${color}33`,
                                    borderRadius: 10,
                                    padding: "10px 20px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: 20,
                                        fontWeight: 700,
                                        color,
                                    }}
                                >
                                    {value ?? 0}
                                </span>
                                <span
                                    style={{
                                        fontSize: 11,
                                        color: "var(--color-text-muted)",
                                    }}
                                >
                                    {label}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                {/* ── Tables ── */}
                <div
                    style={{
                        background: "var(--color-bg-card)",
                        border: "1px solid var(--color-border)",
                        borderRadius: 12,
                        padding: "20px 24px",
                    }}
                >
                    {getUserRole() === "Super Admin" ? (
                        <Tabs
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            type="card"
                            destroyOnHidden
                        >
                            <Tabs.TabPane
                                key="users"
                                tab={
                                    <span
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 6,
                                        }}
                                    >
                                        <TeamOutlined /> Users
                                    </span>
                                }
                            >
                                {isUsersLoading ? (
                                    <Skeleton
                                        active
                                        title={false}
                                        paragraph={{ rows: 10, width: "100%" }}
                                    />
                                ) : (
                                    <CustomTable
                                        columns={columns}
                                        tableData={userData}
                                        noOfrecords={totalRecords}
                                        handleTableChange={handleTableChange}
                                        rowKey="id"
                                        className="dashboard-table"
                                        isScroll
                                    />
                                )}
                            </Tabs.TabPane>

                            <Tabs.TabPane
                                key="unverifiedUsers"
                                tab={
                                    <span
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 6,
                                        }}
                                    >
                                        <SafetyOutlined />
                                        Pending Approval
                                        {totalUnVerifiedRecords > 0 && (
                                            <span
                                                style={{
                                                    background:
                                                        "var(--color-warning)",
                                                    color: "var(--color-text-on-primary)",
                                                    borderRadius: 10,
                                                    fontSize: 10,
                                                    padding: "1px 6px",
                                                    fontWeight: 700,
                                                }}
                                            >
                                                {totalUnVerifiedRecords}
                                            </span>
                                        )}
                                    </span>
                                }
                            >
                                {isUnverifiedLoading ? (
                                    <Skeleton
                                        active
                                        title={false}
                                        paragraph={{ rows: 10, width: "100%" }}
                                    />
                                ) : (
                                    <CustomTable
                                        columns={unvarifiedUserColumns}
                                        tableData={unverifiedUsersData}
                                        noOfrecords={totalUnVerifiedRecords}
                                        rowKey="id"
                                        className="dashboard-table"
                                        isScroll
                                    />
                                )}
                            </Tabs.TabPane>
                        </Tabs>
                    ) : isUsersLoading ? (
                        <Skeleton
                            active
                            title={false}
                            paragraph={{ rows: 10, width: "100%" }}
                        />
                    ) : (
                        <CustomTable
                            columns={columns}
                            tableData={userData}
                            noOfrecords={totalRecords}
                            handleTableChange={handleTableChange}
                            rowKey="id"
                            className="dashboard-table"
                            isScroll
                        />
                    )}
                </div>
            </Card>

            {/* ── Add / Edit User Modal ── */}
            <Modal
                title={<ModalHeader icon={<UserOutlined />} title={title} />}
                open={visible}
                footer={null}
                onCancel={closeModel}
                destroyOnHidden
                width={520}
                styles={modalStyles}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    autoComplete="off"
                    style={{ marginTop: 8 }}
                >
                    {/* Platform */}
                    <Form.Item
                        label={<FormLabel>Platform</FormLabel>}
                        name="platform"
                        rules={[
                            {
                                required: true,
                                message: "Please select a platform.",
                            },
                        ]}
                    >
                        <Select
                            onChange={updateUserRolesFromPlatform}
                            style={inputStyle}
                        >
                            {platforms.map(({ key, name }) => (
                                <Option
                                    key={key}
                                    value={key}
                                    disabled={key > 5}
                                >
                                    {name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {/* Analytics / Vendor — First + Last Name */}
                    <Form.Item
                        shouldUpdate={(p, c) => p.platform !== c.platform}
                        noStyle
                    >
                        {({ getFieldValue }) =>
                            getFieldValue("platform") < 3 && (
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        gap: "0 16px",
                                    }}
                                >
                                    <Form.Item
                                        label={
                                            <FormLabel>First Name</FormLabel>
                                        }
                                        name="first_name"
                                        rules={[
                                            {
                                                required: true,
                                                message: "Required",
                                            },
                                        ]}
                                    >
                                        <Input
                                            prefix={
                                                <UserOutlined
                                                    style={{
                                                        color: "var(--color-text-muted)",
                                                    }}
                                                />
                                            }
                                            placeholder="First name"
                                            style={inputStyle}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label={<FormLabel>Last Name</FormLabel>}
                                        name="last_name"
                                        rules={[
                                            {
                                                required: true,
                                                message: "Required",
                                            },
                                        ]}
                                    >
                                        <Input
                                            placeholder="Last name"
                                            style={inputStyle}
                                        />
                                    </Form.Item>
                                </div>
                            )
                        }
                    </Form.Item>

                    {/* GIT — Name + Username */}
                    <Form.Item
                        shouldUpdate={(p, c) => p.platform !== c.platform}
                        noStyle
                    >
                        {({ getFieldValue }) =>
                            getFieldValue("platform") == 4 && (
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        gap: "0 16px",
                                    }}
                                >
                                    <Form.Item
                                        label={<FormLabel>Name</FormLabel>}
                                        name="name"
                                        rules={[
                                            {
                                                required: true,
                                                message: "Required",
                                            },
                                        ]}
                                    >
                                        <Input
                                            placeholder="Name"
                                            style={inputStyle}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label={<FormLabel>Username</FormLabel>}
                                        name="username"
                                        rules={[
                                            {
                                                required: true,
                                                message: "Required",
                                            },
                                        ]}
                                    >
                                        <Input
                                            placeholder="Username"
                                            style={inputStyle}
                                        />
                                    </Form.Item>
                                </div>
                            )
                        }
                    </Form.Item>

                    {/* Email */}
                    <Form.Item
                        label={<FormLabel>Email</FormLabel>}
                        name="email"
                        rules={[
                            { required: true, message: "Please enter email." },
                        ]}
                    >
                        <Input
                            prefix={
                                <MailOutlined
                                    style={{ color: "var(--color-text-muted)" }}
                                />
                            }
                            placeholder="user@company.com"
                            style={inputStyle}
                        />
                    </Form.Item>

                    {/* Set Password toggle (edit mode) */}
                    <Form.Item
                        shouldUpdate={(p, c) =>
                            p.id !== c.id || p.platform !== c.platform
                        }
                        noStyle
                    >
                        {({ getFieldValue }) =>
                            getFieldValue("id") != undefined &&
                            getFieldValue("platform") != 3 &&
                            getFieldValue("platform") != 5 && (
                                <Form.Item
                                    label={
                                        <FormLabel>Change Password</FormLabel>
                                    }
                                    name="setPassword"
                                    valuePropName="checked"
                                >
                                    <Checkbox
                                        style={{
                                            color: "var(--color-text-secondary)",
                                        }}
                                    >
                                        Set new password
                                    </Checkbox>
                                </Form.Item>
                            )
                        }
                    </Form.Item>

                    {/* Password */}
                    <Form.Item
                        shouldUpdate={(p, c) =>
                            p.setPassword !== c.setPassword ||
                            p.platform !== c.platform
                        }
                        noStyle
                    >
                        {({ getFieldValue }) =>
                            (getFieldValue("setPassword") ||
                                getFieldValue("id") == undefined) &&
                            getFieldValue("platform") != 3 &&
                            getFieldValue("platform") != 5 &&
                            getFieldValue("platform") != 6 && (
                                <Form.Item
                                    label={<FormLabel>Password</FormLabel>}
                                    name="password"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Please enter password.",
                                        },
                                        {
                                            pattern:
                                                /^(?=.*\d)(?=.*[~!@#$%^&*()\-_=+<>,.?/;:"'{}|\\[\]])(?!.*\s)(?=.*[A-Z])(?=.*[a-z]).{8,16}$/,
                                            message:
                                                "8–16 chars with uppercase, lowercase, number & special character.",
                                        },
                                    ]}
                                >
                                    <Input.Password
                                        prefix={
                                            <LockOutlined
                                                style={{
                                                    color: "var(--color-text-muted)",
                                                }}
                                            />
                                        }
                                        placeholder="Password"
                                        style={inputStyle}
                                    />
                                </Form.Item>
                            )
                        }
                    </Form.Item>

                    {/* Status (Analytics only) */}
                    <Form.Item
                        shouldUpdate={(p, c) => p.platform !== c.platform}
                        noStyle
                    >
                        {({ getFieldValue }) =>
                            getFieldValue("platform") == 1 && (
                                <Form.Item
                                    label={<FormLabel>Status</FormLabel>}
                                    name="is_active"
                                    valuePropName="checked"
                                >
                                    <Switch size="small" />
                                </Form.Item>
                            )
                        }
                    </Form.Item>

                    {/* Vendor User Type */}
                    <Form.Item
                        shouldUpdate={(p, c) => p.platform !== c.platform}
                        noStyle
                    >
                        {({ getFieldValue }) =>
                            getFieldValue("platform") == 2 && (
                                <Form.Item
                                    label={
                                        <FormLabel>Vendor User Type</FormLabel>
                                    }
                                    name="vendor_user_type"
                                    rules={[
                                        { required: true, message: "Required" },
                                    ]}
                                >
                                    <Select
                                        placeholder="Select type"
                                        onChange={
                                            updateUserRoleFormVendorUserType
                                        }
                                    >
                                        <Option value={1}>Master User</Option>
                                        <Option value={2}>Hotel User</Option>
                                    </Select>
                                </Form.Item>
                            )
                        }
                    </Form.Item>

                    {/* Role */}
                    <Form.Item
                        shouldUpdate={(p, c) =>
                            p.platform !== c.platform ||
                            p.vendor_user_type !== c.vendor_user_type
                        }
                        noStyle
                    >
                        {({ getFieldValue }) =>
                            (getFieldValue("platform") < 3 ||
                                getFieldValue("vendor_user_type") == 2) && (
                                <Form.Item
                                    label={<FormLabel>Role</FormLabel>}
                                    name="role_id"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Please select a role.",
                                        },
                                        ({ getFieldValue }) => ({
                                            validator() {
                                                const roleAvailable =
                                                    userRoleOption.find(
                                                        ({ key }) =>
                                                            key ==
                                                            getFieldValue(
                                                                "role_id",
                                                            ),
                                                    );
                                                if (!roleAvailable)
                                                    return Promise.reject(
                                                        "This role is not available. Please select another.",
                                                    );
                                                return Promise.resolve();
                                            },
                                        }),
                                    ]}
                                    valuePropName="defaultValue"
                                >
                                    <Select
                                        allowClear
                                        placeholder="Select role"
                                        disabled={
                                            title !== "Add User" &&
                                            userRole !== "Super Admin"
                                        }
                                    >
                                        {userRoleOption.map((r) => (
                                            <Option key={r.key}>
                                                {r.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            )
                        }
                    </Form.Item>

                    {/* Hotels (Analytics only) */}
                    <Form.Item
                        shouldUpdate={(p, c) => p.platform !== c.platform}
                        noStyle
                    >
                        {({ getFieldValue }) =>
                            getFieldValue("platform") == 1 && (
                                <Form.Item
                                    label={<FormLabel>Hotels</FormLabel>}
                                    name="hotel_ids"
                                    valuePropName="defaultValue"
                                >
                                    <Select
                                        mode="multiple"
                                        allowClear
                                        showSearch
                                        placeholder="Select hotels (leave blank for all)"
                                        filterOption={(input, option) =>
                                            option.children
                                                .toLowerCase()
                                                .includes(input.toLowerCase())
                                        }
                                    >
                                        {hotelOptions.map((h) => (
                                            <Option
                                                key={h.hotel_id}
                                                value={h.hotel_id}
                                            >
                                                {`${h.hotel_id} - ${h.hotel_name}`}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            )
                        }
                    </Form.Item>

                    {/* Hidden id */}
                    <Form.Item
                        name="id"
                        className="hidden"
                        style={{ display: "none" }}
                    >
                        <Input disabled />
                    </Form.Item>

                    {/* Submit */}
                    <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                        <Button
                            onClick={closeModel}
                            style={{
                                flex: 1,
                                height: 40,
                                borderRadius: 9,
                                background: "var(--color-bg-control)",
                                border: "1px solid var(--color-border)",
                                color: "var(--color-text-secondary)",
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            style={{
                                flex: 2,
                                height: 40,
                                borderRadius: 9,
                                background: "var(--gradient-primary)",
                                border: "none",
                                fontWeight: 600,
                                boxShadow: "var(--shadow-glow)",
                            }}
                        >
                            Save User
                        </Button>
                    </div>
                </Form>
            </Modal>

            {/* ── Delete Auth Modal ── */}
            <Modal
                title={
                    <ModalHeader
                        icon={<LockOutlined />}
                        title="Confirm Identity to Delete"
                    />
                }
                open={authVisible}
                footer={null}
                onCancel={closeAuthModel}
                destroyOnHidden
                width={420}
                styles={modalStyles}
            >
                <p
                    style={{
                        fontSize: 13,
                        color: "var(--color-text-muted)",
                        marginBottom: 20,
                    }}
                >
                    Please verify your credentials to confirm this deletion.
                </p>
                <Form
                    form={authForm}
                    layout="vertical"
                    onFinish={(values) =>
                        deleteUser({
                            ...values,
                            password: encrypt(values.password),
                            staff_id,
                        })
                    }
                >
                    <Form.Item
                        label={<FormLabel>Your Email</FormLabel>}
                        name="email"
                        rules={[
                            {
                                required: true,
                                message: "Please provide your email.",
                            },
                        ]}
                    >
                        <Input
                            prefix={
                                <MailOutlined
                                    style={{ color: "var(--color-text-muted)" }}
                                />
                            }
                            placeholder="your@email.com"
                            style={inputStyle}
                        />
                    </Form.Item>
                    <Form.Item
                        label={<FormLabel>Your Password</FormLabel>}
                        name="password"
                        rules={[
                            {
                                required: true,
                                message: "Please provide your password.",
                            },
                        ]}
                    >
                        <Input.Password
                            prefix={
                                <LockOutlined
                                    style={{ color: "var(--color-text-muted)" }}
                                />
                            }
                            placeholder="Password"
                            style={inputStyle}
                        />
                    </Form.Item>
                    <Form.Item name="id" style={{ display: "none" }}>
                        <Input disabled />
                    </Form.Item>
                    <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                        <Button
                            onClick={closeAuthModel}
                            style={{
                                flex: 1,
                                height: 40,
                                borderRadius: 9,
                                background: "var(--color-bg-control)",
                                border: "1px solid var(--color-border)",
                                color: "var(--color-text-secondary)",
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            style={{
                                flex: 2,
                                height: 40,
                                borderRadius: 9,
                                background: "var(--color-error)",
                                border: "none",
                                fontWeight: 600,
                                boxShadow: "var(--shadow-button-primary)",
                            }}
                        >
                            Delete User
                        </Button>
                    </div>
                </Form>
            </Modal>
        </>
    );
}

export { UserList };
